/**
 * Draft-only Optimizely blocks → Optimizely Graph node shape.
 *
 * A block created inside a CMS project has never been published, so it is absent
 * from the Graph response and the id-keyed merge in optimizely-preview.js has
 * nothing to merge into — the block silently never renders. This module rebuilds
 * the Graph node shape from the draft payload
 * (`/api/cms-proxy/projects/:id/blocks`) so authors can preview unpublished
 * content without publishing the project.
 *
 * Placement is the hard part: no draft block at any level records a parent, an
 * area or a region. A block nested inside another draft block is placed by that
 * parent, and a block listed in a draft PAGE is placed by that page — both exact.
 * A block that is neither is placed only when its type name names its area (see
 * `resolvePlacementArea`); otherwise it is left out rather than guessed at.
 */

/* Field names the query generator aliases as `<TypeName><Field>: <Field>`
 * because the same name carries different types across content types
 * (optimizelyQueryGenNA.mjs → conflictingFields). Regenerate with:
 *   grep -ho '[A-Za-z0-9_]*: [A-Za-z0-9_]*' frontend/optimizely/fragments/*.graphql |
 *     awk '{print $2}' | sort -u
 */
const ALIASED_FIELDS = new Set([
    'AudienceTargeting',
    'Autoplay',
    'BackgroundMedia',
    'BadgeTextStyle',
    'BorderRadius',
    'CarouselTitleStyle',
    'Content',
    'CTA',
    'Ctas',
    'CTAs',
    'CTATextStyle',
    'Description',
    'DescriptionStyle',
    'DisclaimerStyle',
    'Heading',
    'HidePlayPauseIcon',
    'InfiniteMode',
    'Link',
    'MarginPerDevice',
    'NewsletterSubscription',
    'Notes',
    'Opacity',
    'Padding',
    'PaddingPerDevice',
    'PreTitleStyle',
    'PriceStyle',
    'ProductPriceStyle',
    'ProductTitleStyle',
    'Rows',
    'RowTitle',
    'SectionTitle',
    'ShowSubcategories',
    'StylesPerViewType',
    'Styling',
    'Subtitle',
    'SubtitleStyle',
    'TextStyle',
    'TimeBetweenSlides',
    'Title',
    'TitleStyle',
    'TooltipStyle',
    'Width'
])

/* Draft bookkeeping with no Graph counterpart. `type` is handled separately: on a
 * block it becomes __typename, on a property object it is the Optimizely
 * property-type name (e.g. GDICtaStylesPerViewType) and is simply dropped. */
const DRAFT_META_KEYS = new Set([
    'id',
    'guid',
    'name',
    'metadata',
    'type',
    'status',
    'pageType',
    'urlSegment',
    'urlPath'
])

/* Types the generator skips — they resolve through the hand-written MediaFragment
 * (`Url Title TitleInFrench`), which aliases nothing. */
const UNALIASED_TYPES = new Set(['ImageFile', 'VideoFile', 'SiteDefinition'])

const isUpperLetter = (char) => !!char && char !== char.toLowerCase()

/* Inverse of the draft API's .NET camelCase policy, which lower-cases the whole
 * leading capital run (`CTAsGroupingDisplay` → `ctAsGroupingDisplay`). An all-caps
 * field with no trailing lowercase (`CTA`) is not recoverable and stays `Cta`. */
export const toPascal = (key) => {
    const chars = [...key]
    for (let i = 0; i < chars.length; i++) {
        if (i > 0 && !isUpperLetter(chars[i + 1])) break
        chars[i] = chars[i].toUpperCase()
    }
    return chars.join('')
}

const graphqlKey = (ownerType, key) => {
    const pascal = toPascal(key)
    const aliased = ALIASED_FIELDS.has(pascal) && !UNALIASED_TYPES.has(ownerType)
    return aliased ? `${ownerType}${pascal}` : pascal
}

/* A draft node is a content block — rather than a plain property object such as
 * a style entry — when it carries a guid. Blocks become ContentLink references. */
export const isDraftBlock = (value) =>
    !!value && typeof value === 'object' && !Array.isArray(value) && typeof value.guid === 'string'

const toContentReference = (draft) => ({
    Id: draft.id ?? null,
    WorkId: 0,
    GuidValue: draft.guid ?? null,
    ProviderName: null,
    Url: draft.url ?? null,
    Expanded: convertDraftBlock(draft)
})

export const toContentAreaItem = (draft) => ({
    DisplayOption: '',
    Tag: null,
    ContentLink: toContentReference(draft)
})

const convertDraftValue = (value, ownerType) => {
    if (value === null || typeof value !== 'object') return value

    // CMS richtext arrives as {fragments: [{internalFormat}]}; Graph serves HTML.
    if (Array.isArray(value.fragments)) {
        return value.fragments.map((fragment) => fragment?.internalFormat || '').join('')
    }

    if (Array.isArray(value)) {
        return value.map((entry) =>
            isDraftBlock(entry) ? toContentAreaItem(entry) : convertDraftValue(entry, ownerType)
        )
    }

    // A single referenced block is a ContentReference (e.g. GDIImageBlock.Image),
    // not a content area — it has no DisplayOption/Tag wrapper.
    if (isDraftBlock(value)) return toContentReference(value)

    return convertDraftProperties(value, ownerType)
}

export const convertDraftProperties = (draft, ownerType) => {
    const converted = {}
    for (const [key, value] of Object.entries(draft)) {
        if (DRAFT_META_KEYS.has(key)) continue
        converted[graphqlKey(ownerType, key)] = convertDraftValue(value, ownerType)
    }
    return converted
}

/**
 * Rebuild one draft block as an Optimizely Graph node.
 * @param {Object} draft block from the draft-projects payload
 * @returns {Object} node shaped like a Graph `ContentLink.Expanded`
 */
export const convertDraftBlock = (draft) => {
    if (!draft || typeof draft !== 'object') return draft
    const typename = draft.type
    return {
        __typename: typename,
        ContentLink: {
            Id: draft.id ?? null,
            WorkId: 0,
            GuidValue: draft.guid ?? null,
            ProviderName: null,
            Url: null,
            Expanded: null
        },
        ParentLink: null,
        Url: null,
        ...convertDraftProperties(draft, typename)
    }
}

/* ---------------------------------------------------------------- placement */

const isContentAreaEntry = (entry) => !!entry && typeof entry === 'object' && !!entry.ContentLink

/** Keys of every content area on a Graph node. */
export const collectContentAreas = (item) => {
    const areas = new Set()
    if (!item || typeof item !== 'object') return areas
    for (const [key, value] of Object.entries(item)) {
        if (!Array.isArray(value)) continue
        if (value.length > 0 && !value.some((entry) => isContentAreaEntry(entry))) continue
        areas.add(key)
    }
    return areas
}

/**
 * Which content area a draft-only block belongs to, from its type name alone:
 * `<PageType><Area>Block` (GDIPLPPage + GridInterruptors + Block) can only live
 * in that one area of that one page type, so the placement is exact.
 *
 * Nothing else is inferred. Matching on "an area that already holds this type"
 * was tried and is wrong: a project spans many pages, so a drafted
 * GDIContentTileBlock destined for the PLP matches MainRegion on the homepage
 * just as well and renders on the wrong page. Blocks that resolve to nothing are
 * left out and reach preview only through a page draft (`applyPreviewPages`).
 *
 * @returns {string | null} area key on the page item
 */
export const resolvePlacementArea = (pageTypename, areas, draftTypename) => {
    if (!pageTypename || !draftTypename?.startsWith(pageTypename)) return null
    const suffix = draftTypename.slice(pageTypename.length).replace(/Block$/, '')
    return suffix && areas.has(suffix) ? suffix : null
}

/** Ids of draft blocks nested inside another draft block — placed by their parent. */
const collectNestedDraftIds = (draftBlocks) => {
    const nested = new Set()
    const walk = (node, isRoot) => {
        if (Array.isArray(node)) {
            node.forEach((entry) => walk(entry, false))
            return
        }
        if (!node || typeof node !== 'object') return
        if (!isRoot && isDraftBlock(node) && node.id != null) nested.add(node.id)
        Object.values(node).forEach((value) => walk(value, false))
    }
    draftBlocks.forEach((block) => walk(block, true))
    return nested
}

/** Every `ContentLink.Id` already present in the Graph payload. */
const collectPublishedIds = (content) => {
    const ids = new Set()
    const walk = (node) => {
        if (Array.isArray(node)) {
            node.forEach(walk)
            return
        }
        if (!node || typeof node !== 'object') return
        const id = node?.ContentLink?.Id
        if (id != null) ids.add(id)
        Object.values(node).forEach(walk)
    }
    walk(content)
    return ids
}

const insertIntoItem = (item, draftBlocks, publishedIds, nestedIds) => {
    const areas = collectContentAreas(item)
    if (areas.size === 0) return item

    const additions = {}
    for (const draft of draftBlocks) {
        if (draft?.id == null) continue
        // Already in the payload (step 1 merges it) or placed by a draft parent.
        if (publishedIds.has(draft.id) || nestedIds.has(draft.id)) continue

        const area = resolvePlacementArea(item.__typename, areas, draft.type)
        if (!area) continue
        if (!additions[area]) additions[area] = []
        additions[area].push(toContentAreaItem(draft))
    }

    const changed = Object.keys(additions)
    if (changed.length === 0) return item

    const next = {...item}
    for (const area of changed) {
        next[area] = [...(Array.isArray(item[area]) ? item[area] : []), ...additions[area]]
    }
    return next
}

/**
 * Append draft-only blocks to the page areas they resolve to, on any page type
 * and any region.
 *
 * Audience targeting is NOT applied to inserted blocks: the draft payload omits
 * `AudienceTargeting.Type`, so there is nothing to evaluate. Preview therefore
 * shows every draft variant, matching how it already treats A/B blocks.
 *
 * @param {Object} content page payload (`{item}` or legacy `{items}`)
 * @param {Array} draftBlocks top-level blocks from the draft-projects payload
 * @returns {Object} new payload with draft-only blocks spliced in
 */
export const insertDraftOnlyBlocks = (content, draftBlocks) => {
    if (!content || !Array.isArray(draftBlocks) || draftBlocks.length === 0) return content

    const publishedIds = collectPublishedIds(content)
    const nestedIds = collectNestedDraftIds(draftBlocks)
    const insert = (item) => insertIntoItem(item, draftBlocks, publishedIds, nestedIds)

    if (content.item) return {...content, item: insert(content.item)}
    if (Array.isArray(content.items)) return {...content, items: content.items.map(insert)}
    return content
}
